// File: /src/components/finance/FinanceDashTable.js

import React, { useState } from 'react';
import { FaEdit, FaTrashAlt, FaSortUp, FaSortDown } from 'react-icons/fa';
import '../../styles/finance/FinanceDashTable.css';

function FinanceDashTable({
	allEntries,
	openModalForCreation,
	startEditingEntry,
	deleteEntry,
}) {
	const [selectedMonth, setSelectedMonth] = useState('');
	const [filterStartDate, setFilterStartDate] = useState(null);
	const [filterEndDate, setFilterEndDate] = useState(null);
	const [searchTerm, setSearchTerm] = useState('');

	// Adicionar estado para ordenação
	const [sortConfig, setSortConfig] = useState({
		key: 'date',
		direction: 'asc',
	});

	// Função para ordenar
	const requestSort = (key) => {
		let direction = 'asc';
		if (sortConfig.key === key && sortConfig.direction === 'asc') {
			direction = 'desc';
		}
		setSortConfig({ key, direction });
	};

	// Renderizar ícone de ordenação
	const renderSortIcon = (key) => {
		if (sortConfig.key === key) {
			return sortConfig.direction === 'asc' ? (
				<FaSortUp style={{ marginLeft: '5px' }} />
			) : (
				<FaSortDown style={{ marginLeft: '5px' }} />
			);
		}
		return null;
	};

	const months = [
		{ value: 0, label: 'Janeiro' },
		{ value: 1, label: 'Fevereiro' },
		{ value: 2, label: 'Março' },
		{ value: 3, label: 'Abril' },
		{ value: 4, label: 'Maio' },
		{ value: 5, label: 'Junho' },
		{ value: 6, label: 'Julho' },
		{ value: 7, label: 'Agosto' },
		{ value: 8, label: 'Setembro' },
		{ value: 9, label: 'Outubro' },
		{ value: 10, label: 'Novembro' },
		{ value: 11, label: 'Dezembro' },
	];

	// Primeiro filtra apenas os tipos desejados
	const filteredByType = allEntries.filter(
		(entry) =>
			entry.type === 'income' ||
			entry.type === 'expense' ||
			entry.type === 'invoice'
	);

	// Depois aplica os filtros de data
	const filteredEntries = filteredByType.filter((entry) => {
		const entryDate = new Date(entry.date);
		const matchesSearch = entry.description
			.toLowerCase()
			.includes(searchTerm.toLowerCase());

		if (!matchesSearch) return false;
		if (selectedMonth !== '') {
			if (entryDate.getMonth() !== parseInt(selectedMonth)) return false;
		}
		if (filterStartDate && entryDate < filterStartDate) return false;
		if (filterEndDate && entryDate > filterEndDate) return false;
		return true;
	});

	// Modificar a ordenação dos dados
	const sortedEntries = filteredEntries.sort((a, b) => {
		if (sortConfig.key === 'date') {
			const dateA = new Date(a.date);
			const dateB = new Date(b.date);
			return sortConfig.direction === 'asc'
				? dateA - dateB
				: dateB - dateA;
		}

		if (sortConfig.key === 'value') {
			return sortConfig.direction === 'asc'
				? Number(a.value) - Number(b.value)
				: Number(b.value) - Number(a.value);
		}

		if (a[sortConfig.key] < b[sortConfig.key]) {
			return sortConfig.direction === 'asc' ? -1 : 1;
		}
		if (a[sortConfig.key] > b[sortConfig.key]) {
			return sortConfig.direction === 'asc' ? 1 : -1;
		}
		return 0;
	});

	const netBalance = sortedEntries.reduce((sum, entry) => {
		const value = parseFloat(entry.value) || 0;

		switch (entry.type) {
			case 'income':
			case 'invoice':
				return sum + value;
			case 'expense':
				return sum - value;
			case 'overdue':
			default:
				return sum;
		}
	}, 0);

	const [dateError, setDateError] = useState({ start: '', end: '' });
	const [dateInputValue, setDateInputValue] = useState({
		start: '',
		end: '',
	});

	const formatDateInput = (value) => {
		const digits = value.replace(/\D/g, '');
		let formatted = digits;
		if (digits.length > 2) {
			formatted = digits.slice(0, 2) + '/' + digits.slice(2);
		}
		if (digits.length > 4) {
			formatted =
				digits.slice(0, 2) +
				'/' +
				digits.slice(2, 4) +
				'/' +
				digits.slice(4, 8);
		}
		return formatted;
	};

	const validateDate = (dateStr) => {
		if (dateStr.length === 0) return true;
		const regex = /^(\d{2})\/(\d{2})\/(\d{4})$/;
		if (!regex.test(dateStr)) return false;
		const [, day, month, year] = dateStr.match(regex);
		const date = new Date(year, month - 1, day);
		return (
			date &&
			date.getFullYear() === Number(year) &&
			date.getMonth() === Number(month) - 1 &&
			date.getDate() === Number(day)
		);
	};

	const handleDateChange = (e, field) => {
		const rawValue = e.target.value;
		const formattedValue = formatDateInput(rawValue);
		setDateInputValue((prev) => ({
			...prev,
			[field]: formattedValue,
		}));

		if (!validateDate(formattedValue)) {
			setDateError((prev) => ({
				...prev,
				[field]: 'Data inválida',
			}));
			field === 'start'
				? setFilterStartDate(null)
				: setFilterEndDate(null);
		} else {
			setDateError((prev) => ({
				...prev,
				[field]: '',
			}));
			if (formattedValue.length === 10) {
				const [day, month, year] = formattedValue.split('/');
				const date = new Date(year, month - 1, day);
				field === 'start'
					? setFilterStartDate(date)
					: setFilterEndDate(date);
			}
		}
	};

	return (
		<div className="finance-list">
			<h2>Resumo Financeiro</h2>
			<div className="actions-row">
				{/* Botão de adicionar nova entrada */}
				<button
					className="add-entry-button"
					onClick={openModalForCreation}
				>
					Adicionar Nova Receita/Despesa
				</button>
				{/* Campo de pesquisa */}
				<input
					type="text"
					placeholder="Pesquisar na tabela..."
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					className="search-input"
				/>
			</div>

			{/* Filtros de visualização */}
			<div className="table-filters">
				<div className="filter-item">
					<label>Mês:</label>
					<select
						value={selectedMonth}
						onChange={(e) => {
							setSelectedMonth(e.target.value);
							setFilterStartDate(null);
							setFilterEndDate(null);
						}}
					>
						<option value="">Escolha o mês</option>
						{months.map((month) => (
							<option key={month.value} value={month.value}>
								{month.label}
							</option>
						))}
					</select>
				</div>
				<div className="filter-item">
					<label>Data Inicial:</label>
					<div className="date-input-container">
						<input
							type="text"
							placeholder="DD/MM/AAAA"
							value={dateInputValue.start}
							onChange={(e) => handleDateChange(e, 'start')}
							autoComplete="off"
						/>
						{dateError.start && (
							<span className="date-error">
								{dateError.start}
							</span>
						)}
					</div>
				</div>
				<div className="filter-item">
					<label>Data Final:</label>
					<div className="date-input-container">
						<input
							type="text"
							placeholder="DD/MM/AAAA"
							value={dateInputValue.end}
							onChange={(e) => handleDateChange(e, 'end')}
							autoComplete="off"
						/>
						{dateError.end && (
							<span className="date-error">{dateError.end}</span>
						)}
					</div>
				</div>
			</div>

			<div className="table-wrapper">
				<table className="finance-table">
					<thead>
						<tr>
							<th
								onClick={() => requestSort('date')}
								style={{ cursor: 'pointer' }}
							>
								Data {renderSortIcon('date')}
							</th>
							<th
								onClick={() => requestSort('description')}
								style={{ cursor: 'pointer' }}
							>
								Descrição {renderSortIcon('description')}
							</th>
							<th
								onClick={() => requestSort('type')}
								style={{ cursor: 'pointer' }}
							>
								Tipo {renderSortIcon('type')}
							</th>
							<th
								onClick={() => requestSort('value')}
								style={{ cursor: 'pointer' }}
							>
								Valor (R$) {renderSortIcon('value')}
							</th>
							<th>Ações</th>
						</tr>
					</thead>
					<tbody>
						{sortedEntries.length === 0 ? (
							<tr>
								<td colSpan="5">
									Nenhuma entrada/fatura registrada neste
									período.
								</td>
							</tr>
						) : (
							sortedEntries.map((entry) => {
								const isInvoice = entry.type === 'invoice';
								const isIncome = entry.type === 'income';
								const isExpense = entry.type === 'expense';
								const formattedDate = new Date(
									entry.date
								).toLocaleDateString('pt-BR');
								const canEditOrDelete = isIncome || isExpense;
								return (
									<tr key={entry.id}>
										<td>{formattedDate}</td>
										<td>{entry.description}</td>
										<td
											className={
												isIncome
													? 'amount'
													: isExpense
													? 'expense'
													: 'invoice-paid'
											}
										>
											{isInvoice
												? 'Fatura Paga'
												: isIncome
												? 'Receita'
												: 'Despesa'}
										</td>
										<td
											className={
												isIncome || isInvoice
													? 'amount'
													: 'expense'
											}
										>
											{Number(entry.value).toLocaleString(
												'pt-BR',
												{
													minimumFractionDigits: 2,
													maximumFractionDigits: 2,
												}
											)}
										</td>
										<td>
											{canEditOrDelete && (
												<div className="action-icons">
													<FaEdit
														className="edit-icon"
														onClick={() =>
															startEditingEntry(
																entry
															)
														}
														title="Editar"
													/>
													<FaTrashAlt
														className="delete-icon"
														onClick={() =>
															deleteEntry(entry)
														}
														title="Excluir"
													/>
												</div>
											)}
										</td>
									</tr>
								);
							})
						)}
						{/* Linha de saldo líquido */}
						{sortedEntries.length > 0 && (
							<tr className="net-balance-row">
								<td
									colSpan="3"
									style={{
										textAlign: 'right',
										fontWeight: 'bold',
									}}
								>
									Saldo Líquido:
								</td>
								<td colSpan="2" style={{ fontWeight: 'bold' }}>
									{netBalance.toLocaleString('pt-BR', {
										minimumFractionDigits: 2,
										maximumFractionDigits: 2,
									})}
								</td>
							</tr>
						)}
					</tbody>
				</table>
			</div>
		</div>
	);
}

export default FinanceDashTable;
